import React, { memo, useContext, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import Barcode from 'react-barcode';
import { fetchPostData } from '@/Components/hooks/Api';
import { toastifyError } from '@/Components/Common/AlertMsg';
import { getShowingDateText } from '@/Components/Common/Utility';
import { AgencyContext } from 'Context/Agency';


const BarCode = (props) => {

    const { agencyID, propID, masPropID, codeNo, printStatus, setPrintStatus, isVehicle = false, propIDs = null, codeNos = null } = props;

    const isMulti = propIDs && Array.isArray(propIDs) && propIDs.length > 0;
    const propIDsKey = isMulti ? propIDs.map(String).join(',') : '';
    const { datezone, setChangesStatus, BarCodeStatus, setBarCodeStatus, agencyData } = useContext(AgencyContext);

    const useQuery = () => {
        const params = new URLSearchParams(useLocation().search);
        return {
            get: (param) => params.get(param)
        };
    };

    const query = useQuery();
    let MstVehicle = query?.get('page');
    const isMaster = MstVehicle != '' && MstVehicle === 'MST-Name-Dash' || MstVehicle === 'MST-Property-Dash' || MstVehicle === 'MST-Vehicle-Dash' ? true : false;


    const [barCodeData, setBarCodeData] = useState([])

    const componentRef = useRef();

    const printForm = useReactToPrint({
        content: () => componentRef.current,
        documentTitle: 'Data',
        // copyStyles defaults to true, which clones EVERY <style>/<link> from the whole app
        // into the print iframe and WAITS for each external stylesheet to load before the
        // print dialog opens (this is what made it slow). We instead embed the pre-collected
        // app CSS directly into componentRef below, so we get all styles AND instant opening.
        copyStyles: false,
        // react-to-print injects its OWN pageStyle (default "@page { size: auto }") which
        // otherwise overrides the @page in our content and forces a Letter sheet. Setting it
        // here is the only reliable way to make the printed sheet exactly 4in x 3in.
        pageStyle: `
            @page { size: 4in 3in; margin: 0; }
            @media print {
                html, body {
                    margin: 0 !important;
                    padding: 0 !important;
                    width: 4in !important;
                    background: #fff !important;
                    -webkit-print-color-adjust: exact;
                    print-color-adjust: exact;
                }
                .barcode-print-root { width: 4in !important; padding: 0 !important; margin: 0 !important; }
                .barcode-print-label {
                    width: 4in !important;
                    height: 3in !important;
                    min-height: 3in !important;
                    margin: 0 !important;
                    box-sizing: border-box !important;
                    overflow: hidden !important;
                    page-break-inside: avoid !important;
                    page-break-after: always;
                }
                .barcode-print-label:last-child { page-break-after: auto; }
            }
        `,
        onAfterPrint: () => { '' }
    })

    useEffect(() => {
        if (isMulti && agencyID && propIDs?.length > 0) {
            let cancelled = false;
            const fetchAll = async () => {
                const promises = propIDs.map((pid) => get_BarCode_Data(agencyID, pid, null));
                const results = await Promise.all(promises);
                if (cancelled) return;
                const valid = results.filter(Boolean);
                setBarCodeData(valid);
                setBarCodeStatus(valid.length > 0 ? valid : []);
            };
            fetchAll();
            return () => { cancelled = true; };
        } else if (!isMulti && agencyID && (propID || masPropID)) {
            get_BarCode_Data(agencyID, propID, masPropID).then((res) => {
                // Same shape as multi: barCodeData is an array of Table arrays so map sees barData[0].Property
                if (res && Array.isArray(res) && res.length > 0) {
                    const wrapped = [res];
                    setBarCodeStatus(wrapped);
                    setBarCodeData(wrapped);
                } else {
                    setBarCodeData([]);
                    setBarCodeStatus([]);
                }
            });
        } else if (isMulti && !propIDsKey) {
            setBarCodeData([]);
            setBarCodeStatus([]);
        }
        // propIDsKey is a stable string so effect runs only when IDs actually change, not on every render
    }, [agencyID, propID, masPropID, isMulti, propIDsKey])

    const get_BarCode_Data = async (agencyID, propID, masPropID) => {
        const val = { 'AgencyID': agencyID, 'PropertyID': propID || 0, 'MasterPropertyID': masPropID || 0 }
        const val1 = { 'AgencyID': agencyID, 'PropertyID': 0, 'MasterPropertyID': masPropID || 0 }
        const res = await fetchPostData("PropertyBarCode/GetData_PropertyBarCode", isMaster ? val1 : val);
        return res || null;
    }

    useEffect(() => {
        const canPrintSingle = printStatus && !isMulti && barCodeData?.length > 0;
        const canPrintMulti = printStatus && isMulti && barCodeData?.length === propIDs?.length && barCodeData?.length > 0;
        if (canPrintSingle || canPrintMulti) {
            printForm();
            setPrintStatus(false);
        } else if (printStatus) {
            if (isMulti && (!propIDs?.length || barCodeData?.length !== propIDs?.length)) {
                toastifyError("No Data Available");
            } else if (!isMulti && barCodeData?.length === 0) {
                toastifyError("No Data Available");
            }
            setPrintStatus(false);
        }
    }, [printStatus, isMulti, propIDs, barCodeData?.length])

    const hasPrintContent = barCodeData?.length > 0;
    const totalLabels = hasPrintContent ? barCodeData.reduce((acc, barData) => acc + (barData?.Property?.length > 0 ? barData.Property.length : 1), 0) : 0;
    let labelIndex = 0;

    return (

        (printStatus || hasPrintContent) &&
        <div style={{ position: 'absolute', left: '-9999px', top: 0, zIndex: -1 }} aria-hidden="true">
            <div ref={componentRef} className="barcode-print-root" style={{ width: '100%', padding: '20px', backgroundColor: 'white', visibility: 'visible' }}>
                {/* Label is fully inline-styled, so no app CSS is needed here. These print
                    rules use !important + unique barcode-print-* classes to force the 4in x 3in
                    sheet without affecting any other component's printing. */}
                <style>{`
                    @media print {
                        @page {
                            size: 4in 3in;
                            margin: 0;
                        }
                        html, body {
                            margin: 0 !important;
                            padding: 0 !important;
                            width: 4in !important;
                            background: #fff !important;
                        }
                        .barcode-print-root {
                            width: 4in !important;
                            padding: 0 !important;
                            margin: 0 !important;
                        }
                        .barcode-print-label {
                            width: 4in !important;
                            height: 3in !important;
                            min-height: 3in !important;
                            margin: 0 !important;
                            box-sizing: border-box !important;
                            overflow: hidden !important;
                            page-break-inside: avoid !important;
                            page-break-after: always;
                        }
                        .barcode-print-label:last-child { page-break-after: auto; }
                    }
                `}</style>
                <div>
                    {barCodeData?.length > 0 &&
                        barCodeData?.map((barData, barIndex) => {
                            const items = barData?.[0]?.Property?.length > 0 ? barData?.[0]?.Property : (barData ? [barData?.[0]] : []);
                            return items.map((proData, propIndex) => {
                                labelIndex += 1;
                                const isLastLabel = labelIndex === totalLabels;
                                const incidentNumber = proData?.IncidentNumber || proData?.IncidentNumber || '';
                                const reportedDate = proData?.ReportedDate ? getShowingDateText(proData?.ReportedDate) : ''; const dateValue = barData?.ReportedDtTm || proData?.ReportedDtTm ? getShowingDateText(barData?.ReportedDtTm || proData?.ReportedDtTm) : '08/08/2024 24:36';
                                const codeForLabel = isMulti ? (codeNos?.[barIndex] ?? proData?.PropertyNumber) : codeNo;
                                const prnCode = codeForLabel || `PRN-${String(proData?.PropertyID || 210).padStart(6, '0')}`;
                                return (
                                    <div
                                        key={`${barIndex}-${propIndex}`}
                                        style={{
                                            width: '4in',
                                            minHeight: '3in',
                                            height: '3in',
                                            border: '2px solid black',
                                            padding: '3px',
                                            marginBottom: '10px',
                                            backgroundColor: 'white',
                                            boxSizing: 'border-box',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            fontSize: '10px',
                                            fontFamily: 'Arial, sans-serif',
                                            pageBreakInside: 'avoid',
                                            pageBreakAfter: isLastLabel ? 'auto' : 'always'
                                        }}
                                        className={`barcode-print-label ${isLastLabel ? '' : 'barcode-label-page'}`}
                                    >
                                        {/* Header Section */}
                                        <div style={{ marginBottom: '3px', borderBottom: '1px solid black', paddingBottom: '1px' }}>
                                            <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: '1px' }}>
                                                {/* Logo */}
                                                <div style={{
                                                    width: '45px',
                                                    height: '45px',
                                                    border: '2px solid black',
                                                    borderRadius: '50%',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    marginRight: '4px',
                                                    flexShrink: 0,
                                                    backgroundColor: 'white',
                                                    overflow: 'hidden'
                                                }}>
                                                    {barData?.[0]?.Agency_Photo ? (
                                                        <img
                                                            src={barData?.[0]?.Agency_Photo}
                                                            alt="Agency Logo"
                                                            style={{
                                                                width: '100%',
                                                                height: '100%',
                                                                objectFit: 'cover',
                                                                borderRadius: '50%'
                                                            }}
                                                        />
                                                    ) : (
                                                        <svg width="38" height="38" viewBox="0 0 38 38" style={{ overflow: 'visible' }}>
                                                            <circle cx="19" cy="19" r="17" fill="white" stroke="black" strokeWidth="1.5" />
                                                            {/* Shield */}
                                                            <path d="M 19 8 L 12 10 L 12 16 Q 12 22 19 28 Q 26 22 26 16 L 26 10 Z" fill="white" stroke="black" strokeWidth="0.8" />
                                                            {/* Simple eagle representation */}
                                                            <circle cx="19" cy="16" r="3" fill="black" />
                                                            <path d="M 16 14 L 19 12 L 22 14" stroke="black" strokeWidth="0.8" fill="none" />
                                                            <path d="M 16 18 L 19 20 L 22 18" stroke="black" strokeWidth="0.8" fill="none" />
                                                            {/* Text around */}
                                                            <text x="19" y="32" textAnchor="middle" fontSize="5" fontWeight="bold">DOJ</text>
                                                        </svg>
                                                    )}
                                                </div>
                                                {/* Agency Info */}
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ fontSize: '14px', fontWeight: 'bold', marginBottom: '1px' }}>
                                                        {barData?.[0]?.Agency_Name || ''}
                                                    </div>
                                                    <div style={{ fontSize: '8px', marginBottom: '1px' }}>
                                                        Address: {barData?.[0]?.Agency_Address1 || ''}
                                                    </div>
                                                    <div style={{ fontSize: '8px', display: 'flex', gap: '12px' }}>
                                                        <span>Phone: {barData?.[0]?.Agency_Phone || ''}</span>
                                                        <span>Fax: {barData?.[0]?.Agency_Fax || ''}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Incident Details Section */}
                                        <div style={{ marginBottom: '2px', border: '1px solid black', padding: '0' }}>
                                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '9px' }}>
                                                <colgroup>
                                                    <col style={{ width: '50%' }} />
                                                    <col style={{ width: '50%' }} />
                                                </colgroup>
                                                <tbody>
                                                    <tr>
                                                        <td style={{ borderRight: '1px solid black', padding: '4px', verticalAlign: 'top' }}>
                                                            <div style={{ fontWeight: 'bold', marginBottom: '1px', display: 'flex', gap: '2px' }}><span>Incident #:</span> <span>{incidentNumber}</span></div>

                                                        </td>
                                                        <td style={{ padding: '4px', verticalAlign: 'top' }}>
                                                            <div style={{ fontWeight: 'bold', marginBottom: '1px', display: 'flex', gap: '2px' }}><span>Reported:</span> <span>{reportedDate}</span></div>
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>

                                        {/* Barcode Section */}
                                        <div style={{ marginBottom: '3px', textAlign: 'center', borderBottom: '1px solid black' }}>
                                            <div style={{ fontSize: '10px', fontWeight: 'bold' }}>
                                                {prnCode}
                                            </div>
                                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', lineHeight: 0, fontSize: 0, marginBottom: '3px' }}>
                                                {(codeForLabel || prnCode) && (
                                                    <Barcode
                                                        value={codeForLabel || prnCode}
                                                        format="CODE128"
                                                        width={2}
                                                        height={30}
                                                        displayValue={false}
                                                        margin={0}
                                                        marginTop={0}
                                                        marginBottom={0}
                                                    />
                                                )}
                                            </div>
                                        </div>

                                        {/* Item Details Section */}
                                        <div style={{ flex: 1, border: '1px solid black', marginBottom: '2px' }}>
                                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '9px' }}>
                                                <colgroup>
                                                    <col style={{ width: '16.67%' }} />
                                                    <col style={{ width: '16.67%' }} />
                                                    <col style={{ width: '16.67%' }} />
                                                    <col style={{ width: '16.67%' }} />
                                                    <col style={{ width: '16.67%' }} />
                                                    <col style={{ width: '16.67%' }} />
                                                </colgroup>
                                                <tbody>
                                                    {/* Row 1: Classification and Category */}
                                                    <tr>
                                                        <td colSpan="3" style={{ borderBottom: '1px solid black', padding: '4px', borderRight: '1px solid black', verticalAlign: 'top' }}>
                                                            <div style={{ fontWeight: 'bold', marginBottom: '2px' }}>Classification:</div>
                                                            <div>{proData?.PropertyClassification_Description || ''}</div>
                                                        </td>
                                                        <td colSpan="3" style={{ borderBottom: '1px solid black', padding: '4px', verticalAlign: 'top' }}>
                                                            <div style={{ fontWeight: 'bold', marginBottom: '2px' }}>Category:</div>
                                                            <div>{proData?.PropertyCategory_Description || ''}</div>
                                                        </td>
                                                    </tr>
                                                    {/* Row 2: Brand and Loss Code */}
                                                    <tr>
                                                        <td colSpan="3" style={{ borderBottom: '1px solid black', padding: '4px', borderRight: '1px solid black', verticalAlign: 'top' }}>
                                                            <div style={{ fontWeight: 'bold', marginBottom: '2px', display: 'flex', gap: '2px' }}><span>{isVehicle ? "Plate Type:" : "Brand:"}</span> <span>{isVehicle ? proData?.PropertyVehiclePlateType_Description || '' : proData?.Brand || ''}</span></div>
                                                        </td>
                                                        <td colSpan="3" style={{ borderBottom: '1px solid black', padding: '4px', verticalAlign: 'top' }}>
                                                            <div style={{ marginBottom: '2px', display: 'flex', gap: '2px' }}><span style={{ fontWeight: 'bold' }}>{isVehicle ? "Plate State & No:" : "Loss Code:"}</span> <span>{isVehicle ? proData?.PropertyVehicleState_Description : proData?.PropertyLossCode_Description || ''}</span></div>
                                                        </td>
                                                    </tr>
                                                    {/* Row 3: Serial Id, Model ID, and Quantity */}
                                                    <tr>
                                                        <td colSpan="2" style={{ borderBottom: '1px solid black', padding: '4px', borderRight: '1px solid black', verticalAlign: 'top' }}>
                                                            <div style={{ fontWeight: 'bold', marginBottom: '2px' }}>{isVehicle ? "VIN:" : "Serial ID:"}</div>
                                                            <div>{isVehicle ? proData?.VIN || "" : proData?.SerialID || ''}</div>
                                                        </td>
                                                        <td colSpan="2" style={{ borderBottom: '1px solid black', padding: '4px', borderRight: '1px solid black', verticalAlign: 'top' }}>
                                                            <div style={{ fontWeight: 'bold', marginBottom: '2px' }}>{isVehicle ? "Loss Code:" : "Model ID:"}</div>
                                                            <div>{isVehicle ? proData?.PropertyLossCode_Description || '' : proData?.ModelID || ''}</div>
                                                        </td>
                                                        <td colSpan="2" style={{ borderBottom: '1px solid black', padding: '4px', verticalAlign: 'top' }}>
                                                            <div style={{ fontWeight: 'bold', marginBottom: '2px' }}>{isVehicle ? "Value:" : "Quantity:"}</div>
                                                            <div>{isVehicle ? proData?.Value || "" : proData?.Quantity || ""}</div>
                                                        </td>
                                                    </tr>
                                                    {/* Row 4: Storage Location */}
                                                    <tr>
                                                        <td colSpan="6" style={{ padding: '4px', verticalAlign: 'top' }}>
                                                            <div style={{ marginBottom: '2px', display: 'flex', gap: '2px' }}><span style={{ fontWeight: 'bold' }}>Storage Location:</span> <span>{proData?.PropertyLocation_Description || ''}</span></div>

                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>

                                        {/* Instruction Section */}
                                        <div style={{ textAlign: 'center', fontSize: '8px', color: '#333', paddingTop: '2px' }}>
                                            Scan barcode to view current storage location in Property System
                                        </div>
                                    </div>
                                );
                            });
                        })}
                </div>
            </div>
        </div>

    )
}

export default memo(BarCode)
